import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useDragControls, useMotionValue, animate as fmAnimate } from "framer-motion";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Store, ChevronRight, TrendingUp, TrendingDown, Star, MapPin, X, LineChart as LineIcon } from "lucide-react";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";

const pick = (lang, hi, en) => (lang === "English" ? en : hi);
const fmtDay = (d) => { try { return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" }); } catch { return d; } };

/* 30-day price trend bottom sheet */
function CropTrendSheet({ crop, language, onClose }) {
  const [t, setT] = useState(null);
  const controls = useDragControls();
  const y = useMotionValue(0);
  useBodyScrollLock(true);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  useEffect(() => {
    api.get(`/market/trend?crop=${encodeURIComponent(crop.crop)}&modal=${crop.modal || crop.lastModal || 0}&market=${encodeURIComponent(crop.market || "")}`)
      .then((r) => setT(r.data)).catch(() => setT(false));
  }, [crop]);
  const endDrag = (e, info) => { if (info.offset.y > 90 || info.velocity.y > 400) onClose(); else fmAnimate(y, 0, { type: "spring", stiffness: 320, damping: 32 }); };
  const up = t && t.change30d > 0;
  const near = t ? (t.points[t.points.length - 1].modal - t.low) / Math.max(1, t.high - t.low) : 0;
  const advice = near >= 0.66
    ? pick(language, "भाव ऊँचे हैं — बेचने का अच्छा समय 🟢", "Prices are high — a good time to sell 🟢")
    : near <= 0.34
      ? pick(language, "भाव नीचे हैं — हो सके तो रुकें 🟠", "Prices are low — hold if you can 🟠")
      : pick(language, "भाव सामान्य हैं — नज़र रखें 🟡", "Prices are steady — keep watching 🟡");

  return (
    <AnimatePresence onExitComplete={() => y.set(0)}>
      <motion.div key="trend-scrim" className="fixed inset-0 z-[10040] bg-black/40 backdrop-blur-[2px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
      <div key="trend-panel" className="fixed inset-0 z-[10050] flex items-end justify-center sm:items-center pointer-events-none">
        <motion.div data-testid="crop-trend-sheet" className="pointer-events-auto w-full sm:max-w-md bg-[#fbf8f0] rounded-t-[28px] sm:rounded-[28px] shadow-2xl max-h-[88vh] flex flex-col"
          style={{ y }} drag="y" dragListener={false} dragControls={controls}
          dragConstraints={{ top: 0, bottom: 0 }} dragElastic={{ top: 0, bottom: 0.7 }} onDragEnd={endDrag}
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 30 }}>
          <div onPointerDown={(e) => controls.start(e)} data-testid="trend-drag-handle" className="pt-3 pb-2 flex justify-center cursor-grab active:cursor-grabbing shrink-0" style={{ touchAction: "none" }}>
            <div className="h-1.5 w-12 rounded-full bg-black/20" />
          </div>
          <div className="overflow-y-auto overscroll-contain px-5 pb-6">
            <div className="flex items-start justify-between mb-1">
              <div>
                <h3 className="font-display font-extrabold text-xl text-[#173f1e]">{crop.crop}</h3>
                {crop.market && <p className="text-[12px] text-[#6c7a67] flex items-center gap-1"><MapPin className="h-3 w-3" />{crop.market}</p>}
              </div>
              <button onClick={onClose} className="h-9 w-9 rounded-full bg-black/5 flex items-center justify-center" data-testid="trend-close"><X className="h-4 w-4" /></button>
            </div>

            {t === null ? (
              <div className="h-56 flex items-center justify-center text-sm text-[#6c7a67]">{pick(language, "लोड हो रहा…", "Loading…")}</div>
            ) : t === false ? (
              <div className="h-40 flex items-center justify-center text-sm text-[#6c7a67]">{pick(language, "भाव उपलब्ध नहीं", "Trend unavailable")}</div>
            ) : (
              <>
                <div className="flex items-baseline gap-2 mt-1">
                  <p className="font-display font-extrabold text-2xl text-[#173f1e]">₹{t.points[t.points.length - 1].modal.toLocaleString("en-IN")}<span className="text-[12px] text-[#9aa79a] font-normal">/qtl</span></p>
                  <p className={`text-sm font-semibold flex items-center gap-0.5 ${up ? "text-[#2f9e3a]" : "text-rose-500"}`}>{up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}{Math.abs(t.change30d)}% <span className="text-[#9aa79a] font-normal">· 30d</span></p>
                </div>

                <div className="h-52 -mx-2 mt-3" data-testid="crop-trend-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={t.points} margin={{ top: 8, right: 10, left: 6, bottom: 0 }}>
                      <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 10, fill: "#9aa79a" }} axisLine={false} tickLine={false} minTickGap={40} />
                      <YAxis domain={["dataMin - 100", "dataMax + 100"]} tick={{ fontSize: 10, fill: "#9aa79a" }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `₹${(v / 1000).toFixed(1)}k`} />
                      <Tooltip formatter={(v) => [`₹${Number(v).toLocaleString("en-IN")}`, pick(language, "भाव", "Price")]} labelFormatter={fmtDay} contentStyle={{ borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }} />
                      <ReferenceLine y={t.avg} stroke="#b06a2c" strokeDasharray="4 4" strokeOpacity={0.5} />
                      <Line type="monotone" dataKey="modal" stroke="#2f7d33" strokeWidth={2.5} dot={false} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div className="rounded-2xl bg-white/70 border border-black/5 p-2.5 text-center"><p className="text-[10px] text-[#9aa79a]">{pick(language, "30-दिन सबसे कम", "30d low")}</p><p className="font-bold text-[#173f1e] tabular-nums">₹{t.low.toLocaleString("en-IN")}</p></div>
                  <div className="rounded-2xl bg-white/70 border border-black/5 p-2.5 text-center"><p className="text-[10px] text-[#9aa79a]">{pick(language, "औसत", "Average")}</p><p className="font-bold text-[#b06a2c] tabular-nums">₹{t.avg.toLocaleString("en-IN")}</p></div>
                  <div className="rounded-2xl bg-white/70 border border-black/5 p-2.5 text-center"><p className="text-[10px] text-[#9aa79a]">{pick(language, "30-दिन सबसे ज़्यादा", "30d high")}</p><p className="font-bold text-[#173f1e] tabular-nums">₹{t.high.toLocaleString("en-IN")}</p></div>
                </div>

                <div className="rounded-2xl bg-[#2f7d33]/8 border border-[#2f7d33]/15 p-3 mt-3 flex items-start gap-2" data-testid="trend-advice">
                  <LineIcon className="h-4 w-4 text-[#2f7d33] mt-0.5 shrink-0" />
                  <p className="text-[13px] font-medium text-[#173f1e]">{advice}</p>
                </div>
                {t.estimated && <p className="text-[10px] text-[#9aa79a] mt-2">{pick(language, "* कुछ दिन अनुमानित (जब लाइव मंडी डेटा नहीं था)।", "* Some days are estimated (when live mandi data wasn't recorded).")}</p>}
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

export function MyCropRates({ mycrops, language, item }) {
  const [fav, setFav] = useState(() => JSON.parse(localStorage.getItem("am_fav_crops") || "[]"));
  const [trendCrop, setTrendCrop] = useState(null);
  const [signals, setSignals] = useState({});
  const crops = mycrops?.crops || [];
  useEffect(() => {
    api.get("/market/sell-signals").then((r) => {
      const m = {};
      (r.data?.signals || []).forEach((s) => { m[s.crop] = s; });
      setSignals(m);
    }).catch(() => {});
  }, []);
  const toggle = (name) => {
    setFav((prev) => {
      const nx = prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name];
      localStorage.setItem("am_fav_crops", JSON.stringify(nx));
      return nx;
    });
  };
  const sorted = [...crops].sort((a, b) => (fav.includes(b.crop) - fav.includes(a.crop)) || (b.available - a.available));
  // sell window = a watched (starred) crop sitting at its 30-day high
  const sellWindow = crops.find((c) => fav.includes(c.crop) && signals[c.crop]?.atHigh)
    || crops.find((c) => signals[c.crop]?.atHigh);

  return (
    <motion.div variants={item} className="agro-glass rounded-[22px] p-5" data-testid="mycrops-rates-card">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display font-bold text-[17px] text-[#173f1e] flex items-center gap-2"><Store className="h-5 w-5 text-[#b06a2c]" />{pick(language, "मेरी फसलों के भाव", "My Crop Rates")}</h2>
        <Link to="/market" className="text-sm font-semibold text-[#2f7d33] flex items-center tap" data-testid="mycrops-see-all">{pick(language, "मंडी", "Mandi")}<ChevronRight className="h-4 w-4" /></Link>
      </div>
      <p className="text-[11px] text-[#6c7a67] mb-3">{pick(language, "लाइव मंडी भाव — फसल पर टैप करके 30-दिन का चार्ट देखें", "Live mandi rates — tap a crop for its 30-day trend")}</p>

      {sellWindow && (
        <button onClick={() => setTrendCrop(sellWindow)} data-testid="sell-window-banner"
          className="w-full flex items-center gap-2.5 rounded-2xl bg-gradient-to-r from-[#1e874b] to-[#2f9e3a] text-white px-3.5 py-3 mb-3 text-left active:scale-[0.99] transition-transform">
          <span className="relative flex h-2.5 w-2.5 shrink-0"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold leading-tight">🟢 {sellWindow.crop} {pick(language, "30-दिन के उच्चतम पर!", "at a 30-day high!")}</p>
            <p className="text-[11px] text-white/85">{pick(language, `₹${(sellWindow.modal || 0).toLocaleString("en-IN")}/क्विंटल — बेचने का बढ़िया मौका`, `₹${(sellWindow.modal || 0).toLocaleString("en-IN")}/qtl — great time to sell`)}</p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0" />
        </button>
      )}

      {sorted.length === 0 ? (
        <p className="text-sm text-[#6c7a67] py-4 text-center">{pick(language, "फसल चक्र शुरू करें, भाव यहाँ दिखेंगे।", "Start a crop cycle to see rates here.")}</p>
      ) : (
        <div className="space-y-2">
          {sorted.slice(0, 6).map((c, i) => {
            const up = (c.change || 0) > 0;
            const flat = !c.change;
            const atHigh = signals[c.crop]?.atHigh;
            return (
              <button key={`${c.crop}-${i}`} onClick={() => setTrendCrop(c)} className="w-full flex items-center gap-3 rounded-2xl bg-white/60 p-2.5 border border-black/5 text-left active:scale-[0.99] transition-transform" data-testid={`myrate-${i}`}>
                <span onClick={(e) => { e.stopPropagation(); toggle(c.crop); }} className="p-0.5 shrink-0 tap" data-testid={`myrate-star-${i}`}>
                  <Star className={`h-4 w-4 ${fav.includes(c.crop) ? "fill-[#f2c230] text-[#f2c230]" : "text-[#c3ccbf]"}`} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-[#1c2b1e] truncate flex items-center gap-1">{c.crop}
                    {atHigh ? <span className="text-[9px] font-bold rounded-full bg-[#1e874b]/15 text-[#1e874b] px-1.5 py-0.5" data-testid={`sell-badge-${i}`}>SELL 🟢</span> : <LineIcon className="h-3 w-3 text-[#9aa79a]" />}
                  </p>
                  {c.market && <p className="text-[11px] text-[#6c7a67] flex items-center gap-0.5 truncate"><MapPin className="h-3 w-3 shrink-0" />{c.market}</p>}
                </div>
                {c.available ? (
                  <div className="text-right shrink-0">
                    <p className="font-bold text-[15px] tabular-nums text-[#173f1e]">₹{c.modal?.toLocaleString("en-IN")}<span className="text-[10px] text-[#9aa79a] font-normal">/qtl</span></p>
                    {!flat && (
                      <p className={`text-[11px] font-semibold flex items-center justify-end gap-0.5 ${up ? "text-[#2f9e3a]" : "text-rose-500"}`}>
                        {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(c.change)}%
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-right shrink-0">
                    <p className="text-[11px] text-[#9aa79a]">{pick(language, "आज भाव नहीं", "No rate today")}</p>
                    {c.lastModal && <p className="text-[11px] text-[#6c7a67] tabular-nums">{pick(language, "पिछला", "Last")} ₹{c.lastModal?.toLocaleString("en-IN")}</p>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
      {trendCrop && <CropTrendSheet crop={trendCrop} language={language} onClose={() => setTrendCrop(null)} />}
    </motion.div>
  );
}
